import React from 'react';
import styled from 'styled-components';
import WebRoundedIcon from '@mui/icons-material/WebRounded';
import AccountTreeRoundedIcon from '@mui/icons-material/AccountTreeRounded';
import ImageRoundedIcon from '@mui/icons-material/ImageRounded';
import CodeRoundedIcon from '@mui/icons-material/CodeRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import type { ArtifactContentItem } from '../../shared/interfaces';
import { ARTIFACT_TYPE_LABELS } from './artifactDocument';
import { openArtifact, useArtifactSource, useArtifactViewer } from './artifactViewerStore';

const ICONS: Record<string, React.ReactNode> = {
  html: <WebRoundedIcon fontSize="small" />,
  react: <CodeRoundedIcon fontSize="small" />,
  svg: <ImageRoundedIcon fontSize="small" />,
  mermaid: <AccountTreeRoundedIcon fontSize="small" />,
};

const Card = styled.button<{ $active: boolean }>`
  all: unset;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  max-width: 420px;
  margin: 10px 0;
  padding: 10px 12px;
  border-radius: 12px;
  cursor: pointer;
  font-family: 'Sora', 'Lexend', sans-serif;
  border: 1px solid ${({ theme, $active }) => ($active ? theme.font_color : `${theme.text_secondary}40`)};
  background: ${({ theme }) => theme.user_message_background};
  color: ${({ theme }) => theme.font_color};
  transition: border-color 0.15s ease, transform 0.15s ease;
  &:hover {
    border-color: ${({ theme }) => `${theme.text_secondary}99`};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.blue_color};
    outline-offset: 2px;
  }
`;

const IconBox = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: ${({ theme }) => theme.base_color};
  border: 1px solid ${({ theme }) => `${theme.text_secondary}30`};
`;

const Text = styled.span`
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
`;

const Title = styled.span`
  font-size: 0.92rem;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Meta = styled.span`
  font-size: 0.78rem;
  color: ${({ theme }) => theme.text_secondary};
`;

interface Props {
  data: ArtifactContentItem;
}

/** Cartão do artefato na mensagem: abre o painel lateral com a versão que a mensagem produziu. */
const ArtifactCard: React.FC<Props> = ({ data }) => {
  const source = useArtifactSource();
  const { ref } = useArtifactViewer();
  const active = ref?.artifactId === data.artifact_id && (ref?.version ?? 0) === data.version;
  const label = ARTIFACT_TYPE_LABELS[data.artifact_type] || 'Artefato';
  return (
    <Card
      type="button"
      data-download-exclude
      $active={active}
      aria-label={`Abrir artefato ${data.title}`}
      onClick={() =>
        openArtifact({
          artifactId: data.artifact_id,
          title: data.title,
          artifactType: data.artifact_type,
          version: data.version,
          source,
        })
      }
    >
      <IconBox>{ICONS[data.artifact_type] || <WebRoundedIcon fontSize="small" />}</IconBox>
      <Text>
        <Title>{data.title}</Title>
        <Meta>
          {label}
          {data.version > 1 ? ` · versão ${data.version}` : ''}
        </Meta>
      </Text>
      <ChevronRightRoundedIcon fontSize="small" style={{ opacity: 0.6 }} />
    </Card>
  );
};

export default ArtifactCard;
